import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CallWithAuth } from '../../../actions/apiAction';
import { CREATOR_BANK_ACCOUNT } from '../../../utils/apiUrl';
import BankAccountForm from '../../../components/bank/BankAccountForm';
import ResetBankModal from '../../../components/bank/ResetBankModal';

export default function PaymentSetup() {
    const navigate = useNavigate();
    const [bankStatus, setBankStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [editing, setEditing] = useState(false);

    const loadBankStatus = useCallback(async () => {
        setStatusLoading(true);
        try {
            const res = await CallWithAuth('GET', CREATOR_BANK_ACCOUNT);
            if (res?.data) {
                setBankStatus(res.data);
                if (res.data.on_file) setEditing(false);
            }
        } catch (err) {
            console.error('Failed to load bank account', err);
        } finally {
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBankStatus();
    }, [loadBankStatus]);

    const onFile = Boolean(bankStatus?.on_file);
    const showForm = !statusLoading && (!onFile || editing);

    const confirmRemove = async () => {
        await CallWithAuth('DELETE', CREATOR_BANK_ACCOUNT);
        setResetModalOpen(false);
        await loadBankStatus();
    };

    return (
        <>
            <Helmet>
                <title>Payment Setup</title>
            </Helmet>
            <div className="main-body">
                <div className="main-header-s1 center-text">
                    <button type="button" className="back-btn" onClick={() => navigate(-1)}>
                        <img src="/images/icons/chevron-left.svg" alt="" />
                    </button>
                    <h1 className="main-header-s1__title">Payment Setup</h1>
                    <div className="main-header-s1__actions"></div>
                </div>
                <div className="body-content">
                    <div className="my-subscribers-layout">
                        {statusLoading && (
                            <div className="layout-item" style={{ width: '100%' }}>
                                <div className="layout-box widget-type2" style={{ width: '100%' }}>
                                    <div className="widget-header">
                                        <div className="left-div">
                                            <h2>Set up payouts</h2>
                                            <h4>Add a US bank account to get paid for subscriptions</h4>
                                        </div>
                                    </div>
                                    <div
                                        className="widget-body"
                                        style={{
                                            padding: 12,
                                            minHeight: 320,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <div
                                            className="w-10 h-10 rounded-full border-4 border-transparent border-t-[#AF0056] border-r-pink-400 animate-spin"
                                            aria-label="Loading"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {showForm && (
                            <div className="layout-item" style={{ width: '100%' }}>
                                <div className="layout-box widget-type2" style={{ width: '100%' }}>
                                    <div className="widget-header">
                                        <div className="left-div">
                                            <h2>Add a bank account</h2>
                                            <h4>
                                                Your payouts will be sent to this account
                                            </h4>
                                        </div>
                                    </div>
                                    <div className="widget-body" style={{ padding: 16 }}>
                                        <BankAccountForm onSaved={loadBankStatus} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {!statusLoading && onFile && !editing && (
                            <div className="layout-item" style={{ width: '100%' }}>
                                <div className="layout-box widget-type2">
                                    <div className="widget-header payout-ready-header">
                                        <div className="payout-ready-header__top">
                                            <h2>Payouts ready</h2>
                                        </div>
                                        <h4>
                                            Earnings will be paid to {bankStatus.bank_name} ••••
                                            {bankStatus.account_number_last4}.
                                        </h4>
                                    </div>
                                    <div className="widget-body" style={{ padding: 16 }}>
                                        <div className="bank-on-file">
                                            <div className="bank-on-file__row">
                                                <span>Account holder</span>
                                                <strong>{bankStatus.account_holder_name}</strong>
                                            </div>
                                            <div className="bank-on-file__row">
                                                <span>Bank</span>
                                                <strong>{bankStatus.bank_name}</strong>
                                            </div>
                                            <div className="bank-on-file__row">
                                                <span>Routing number</span>
                                                <strong>{bankStatus.routing_number}</strong>
                                            </div>
                                            <div className="bank-on-file__row">
                                                <span>Account</span>
                                                <strong>•••• {bankStatus.account_number_last4}</strong>
                                            </div>
                                            <div className="bank-on-file__row">
                                                <span>Type</span>
                                                <strong>
                                                    {bankStatus.account_type === 'savings'
                                                        ? 'Savings'
                                                        : 'Checking'}
                                                </strong>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="bank-form__secondary"
                                            style={{ marginTop: 16 }}
                                            onClick={() => setEditing(true)}
                                        >
                                            Update bank account
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {!statusLoading && onFile && !editing && (
                        <div className="reset-account-zone">
                            <div className="reset-account-zone__text">
                                <span className="reset-account-zone__title">Made a mistake?</span>
                                <span className="reset-account-zone__desc">
                                    Remove this account and start over.
                                </span>
                            </div>
                            <button
                                type="button"
                                className="reset-account-zone__btn"
                                onClick={() => setResetModalOpen(true)}
                            >
                                Start Over
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ResetBankModal
                open={resetModalOpen}
                onCancel={() => setResetModalOpen(false)}
                onConfirm={confirmRemove}
            />
        </>
    );
}
